import cx from 'classnames'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import {
  HTMLAttributes,
  InputEvent,
  MouseEvent,
  ReactNode,
  useContext,
} from 'react'

import { SanityLink } from '@data/sanity/queries/types/content'
import { getPageUrl, isRouteCollection, PageType } from '@lib/routes'
import { SiteContext } from '@lib/site'
import { useLogoutUser } from '@lib/user/auth'

import Button, {
  getButtonColor,
  getButtonIconAlignment,
  getButtonSize,
  getButtonVariant,
} from '@components/buttons/button'
import ButtonLink from '@components/buttons/button-link'

interface LinkProps {
  link: SanityLink
  onClick?: () => void
  children?: ReactNode
  showCollectionCount?: boolean
}

const Link = ({
  link,
  children,
  tabIndex,
  onClick,
  className,
  showCollectionCount,
  'aria-label': ariaLabel,
}: LinkProps & HTMLAttributes<HTMLAnchorElement>) => {
  const { getProductCount } = useContext(SiteContext)
  const router = useRouter()
  const logoutUser = useLogoutUser()

  const pageType = link.page && (link.page.type as PageType)
  const pageSlug = link.page?.slug
  const isCollection = !!pageType && isRouteCollection(pageType)
  const collectionSlug = isCollection && pageSlug ? pageSlug : 'all'
  const collectionCount = getProductCount(collectionSlug)

  const { url, isButton, buttonStyle } = link
  const buttonStyles = isButton ? buttonStyle : undefined

  // External link
  if (url) {
    return (
      <ButtonLink
        href={url}
        target={url && !url.match('^mailto:') ? '_blank' : '_self'}
        aria-label={ariaLabel}
        tabIndex={tabIndex}
        rel="noopener noreferrer"
        variant={getButtonVariant(buttonStyles?.variant)}
        size={getButtonSize(buttonStyles?.size)}
        color={getButtonColor(buttonStyles?.color)}
        icon={buttonStyles?.icon}
        iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
        className={cx(
          {
            btn: isButton,
            'w-full': buttonStyles?.isFullWidth,
          },
          className,
        )}
      >
        {children}
      </ButtonLink>
    )
  }

  // Internal Page
  if (!pageType) {
    return null
  }

  const pageUrl = getPageUrl(pageType, pageSlug)

  const getClickHandler = (event: MouseEvent<HTMLButtonElement>) => {
    if (pageType === PageType.LOGOUT_PAGE) {
      event.preventDefault()
      logoutUser(() => {
        router.push(getPageUrl(PageType.LOGIN_PAGE))
      })
    }

    onClick && onClick()
  }

  const beforeInputHandler = (event: InputEvent<HTMLButtonElement>) => {
    if (pageType === PageType.LOGOUT_PAGE) {
      event.preventDefault()
      logoutUser(() => {
        router.push(getPageUrl(PageType.LOGIN_PAGE))
      })
    }

    onClick && onClick()
  }

  return (
    <NextLink href={pageUrl}>
      <Button
        role="link"
        tabIndex={tabIndex}
        onClick={getClickHandler}
        onBeforeInput={beforeInputHandler}
        aria-label={ariaLabel}
        variant={getButtonVariant(buttonStyles?.variant)}
        size={getButtonSize(buttonStyles?.size)}
        color={getButtonColor(buttonStyles?.color)}
        icon={buttonStyles?.icon}
        iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
        className={cx(
          {
            btn: isButton,
            'w-full': buttonStyles?.isFullWidth,
          },
          className,
        )}
      >
        {children}

        {showCollectionCount && isCollection && (
          <span
            aria-hidden="true"
            className="inline-block relative ml-2 leading-none align-super text-[.5em]"
          >
            {collectionCount}
          </span>
        )}
      </Button>
    </NextLink>
  )
}

export default Link
